import type {
  BillingPlanSelect,
  BillingSubscriptionSelect,
  BillingSubscriptionStatusSelect,
  UsageSelect,
} from '@urbanmetrix/sdk';
import { apiClient, throwApiError } from './apiClient';

type BillingPlanPrice = BillingPlanSelect['yearly'] & { lookupKey?: string | null };

export type BillingPlan = Omit<BillingPlanSelect, 'yearly'> & {
  yearly: BillingPlanSelect['yearly'] & { lookupKey?: string | null };
  trial?: BillingPlanPrice | null;
  entitlements?: Array<{
    featureId: string;
    lookupKey: string;
    featureName: string;
    livemode: boolean;
  }>;
};
export type PlanPrice = NonNullable<BillingPlanSelect['yearly']>;
export type BillingNextPlan = {
  plan: BillingPlan | null;
  startsAt: string | null;
} | null;
type BillingSubscriptionProjection = Pick<
  BillingSubscriptionSelect,
  'status' | 'canceledAt' | 'cancelAt' | 'billingInterval' | 'periodStart' | 'periodEnd' | 'seats'
> & { planDetails: BillingPlan | null; cancelAtPeriodEnd?: boolean | null };

export type BillingSubscription =
  | (BillingSubscriptionProjection & { nextPlan?: BillingNextPlan })
  | (BillingSubscriptionStatusSelect & Partial<BillingSubscriptionProjection> & { nextPlan?: BillingNextPlan })
  | null;
export type BillingUsage = (UsageSelect & { volumeCredits?: number | null }) | null;
type GetCurrentSubscriptionOptions = {
  includeNextPlan?: boolean;
};

export async function getPlans(): Promise<BillingPlan[]> {
  const response = await apiClient.api.billing.plans.get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data as BillingPlan[];
}

export async function getCurrentSubscription(options?: GetCurrentSubscriptionOptions): Promise<BillingSubscription> {
  const response = await apiClient.api.billing.subscription.get({
    query: options?.includeNextPlan ? { includeNextPlan: 'true' } : {},
  });
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function getCurrentUsage(): Promise<BillingUsage> {
  const response = await apiClient.api.billing.usage.get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}
